/*
 * sticky-atc.js — reveal logic for sections/sticky-add-to-cart.liquid.
 *
 * The adding itself is story-atc.js. This file only decides when each bar is
 * on screen and keeps its price and label in step with the product form.
 *
 * Reveal is per bar, because desktop and mobile are configured separately:
 *   always   shows at once.
 *   scroll   waits until the visitor is both past the main add-to-cart button
 *            and a fifth of the way down the page, so it never competes with
 *            the button it stands in for.
 *   percent  waits for a scroll depth set in the section (data-reveal-percent).
 *   anchor   waits for an element on the page (data-reveal-anchor, usually a
 *            story-row Anchor ID) to come into view or reach the top of the
 *            screen (data-reveal-edge). An anchor that is not on the page
 *            falls back to the scroll rule, so a typo leaves the bar working
 *            instead of never showing it.
 */
if (!customElements.get('sticky-add-to-cart')) {
  // Share of the scrollable page that has to be behind the visitor before a
  // scroll-reveal bar is allowed on screen.
  const SCROLL_REVEAL_THRESHOLD = 0.2;

  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.bars = Array.from(this.querySelectorAll('[data-sticky-bar]'));
        // Opt into the hide-until-revealed styling. Until this class lands the
        // bars are plainly visible, so a script that never runs degrades to a
        // permanently shown bar instead of no bar at all.
        this.classList.add('sticky-atc--js');

        const scrollBars = this.bars.filter((bar) => bar.dataset.reveal && bar.dataset.reveal !== 'always');
        this.bars
          .filter((bar) => !scrollBars.includes(bar))
          .forEach((bar) => bar.classList.add('sticky-atc__bar--revealed'));

        if (scrollBars.length) this.watchScroll(scrollBars);

        this.syncOnVariantChange();
      }

      disconnectedCallback() {
        if (this.onScroll) {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }
        if (this.frame) cancelAnimationFrame(this.frame);
        if (this.unsubscribe) this.unsubscribe();
      }

      watchScroll(bars) {
        // The main buy button, when the page has one.
        this.anchor =
          document.querySelector('product-form .product-form__submit') ||
          document.querySelector('[id^="ProductSubmitButton-"]');

        // Scroll position, not an IntersectionObserver on the main button: the
        // observer could not tell the two sides of that button apart, so on a
        // phone, where the button starts below the fold, "not intersecting"
        // was already true at the top of the page and the bar appeared there.
        this.onScroll = () => {
          if (this.frame) return;
          this.frame = requestAnimationFrame(() => {
            this.frame = null;
            bars.forEach((bar) => bar.classList.toggle('sticky-atc__bar--revealed', this.shouldReveal(bar)));
          });
        };

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        this.onScroll();
      }

      scrollProgress() {
        const scrollable = document.documentElement.scrollHeight - window.innerHeight;
        return scrollable > 0 ? window.scrollY / scrollable : 1;
      }

      shouldReveal(bar) {
        switch (bar.dataset.reveal) {
          case 'percent': {
            const percent = parseFloat(bar.dataset.revealPercent);
            const threshold = Number.isFinite(percent) ? percent / 100 : SCROLL_REVEAL_THRESHOLD;
            return this.scrollProgress() >= threshold;
          }
          case 'anchor': {
            const target = this.resolveAnchor(bar);
            if (!target) return this.pastMainButton();
            const top = target.getBoundingClientRect().top;
            // "view": its top edge has entered the viewport from below.
            // "top" (default): its top edge has reached the top of the screen.
            return bar.dataset.revealEdge === 'view' ? top <= window.innerHeight : top <= 0;
          }
          default:
            return this.pastMainButton();
        }
      }

      pastMainButton() {
        if (this.scrollProgress() < SCROLL_REVEAL_THRESHOLD) return false;

        // No main button to stand in for (a product page without a buy form),
        // so the scroll depth is the whole test.
        if (!this.anchor) return true;

        // Past the main button, not merely far enough down the page: its
        // bottom edge has to have left the top of the viewport.
        return this.anchor.getBoundingClientRect().bottom <= 0;
      }

      // Accepts what a merchant is likely to type: an Anchor ID with or
      // without the leading #, in any case, or a CSS selector. Resolved once
      // per bar; a miss is cached too so a bad value is not re-queried on
      // every scroll frame. A cached element that has left the document (the
      // theme editor re-rendering its section) is looked up again.
      resolveAnchor(bar) {
        if (bar.anchorTarget === null) return null;
        if (bar.anchorTarget && bar.anchorTarget.isConnected) return bar.anchorTarget;

        const raw = (bar.dataset.revealAnchor || '').trim();
        const id = raw.replace(/^#/, '');
        let target = null;
        if (id) target = document.getElementById(id) || document.getElementById(id.toLowerCase());
        if (!target && raw) {
          try {
            target = document.querySelector(raw);
          } catch (error) {
            // Not a valid selector; fall through to the miss below.
          }
        }

        if (!target) {
          console.warn(`sticky-add-to-cart: reveal anchor "${raw}" not found on this page; using the default reveal.`);
        }
        bar.anchorTarget = target;
        return target;
      }

      syncOnVariantChange() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event?.data;
          if (!data?.variant) return;

          // story-atc.js already moved the variant id on the button itself;
          // what is left is the price and the sold-out label.
          const priceSource = data.html?.getElementById(`price-${data.sectionId}`);
          if (priceSource) {
            this.querySelectorAll('[data-sticky-atc-price]').forEach((node) => {
              node.innerHTML = priceSource.innerHTML;
            });
          }

          const available = data.variant.available;
          this.querySelectorAll('.sticky-atc__button').forEach((button) => {
            if (available) {
              button.removeAttribute('aria-disabled');
            } else {
              button.setAttribute('aria-disabled', 'true');
            }

            const label = button.querySelector('[data-sticky-atc-label]');
            if (!label) return;
            if (!available) {
              label.dataset.availableLabel = label.dataset.availableLabel || label.textContent.trim();
              label.textContent = window.variantStrings.soldOut;
            } else if (label.dataset.availableLabel) {
              label.textContent = label.dataset.availableLabel;
            }
          });
        });
      }
    }
  );
}
